import { getPublicEnvironmentValue } from '@pe-libs/react-components/build/lib/environment';
import useIntlMessage from '@pe-libs/react-components/build/lib/intl/hooks/useIntlMessage';
import { useFormik } from 'formik';
import { useRouter } from 'next/router';
import React, { FC, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Cookies from 'universal-cookie';
import { selectIsEmployerHost } from '#modules/app/selectors/settings';
import {
  onboardingFinished,
  registrationDataRequested,
  registrationDataRequestedFallback,
} from '#modules/auth/actions/onboarding';
import { trinityResponseSchema } from '#modules/auth/schemas/onboarding';
import { getOnboardingStatus } from '#modules/auth/selectors';
import yup from '#schema/locale';
import { ThunkAppDispatch } from '../../../../types';
import Error from '../Error';
import PeContentLayout from '../PeContentLayout';
import { Props as PeContentLayoutMarketingBannerProps } from '../PeContentLayout/PeContentLayoutMarketingBanner';
import { validatePassword } from './helper';
import messages from './messages';
import PeOnboardingError from './PeOnboardingError';
import PeOnboardingWizard from './PeOnboardingWizard';

type BackgroundMapType = {
  [key: number]: PeContentLayoutMarketingBannerProps['backgroundName'];
};

const backgroundStepMap: BackgroundMapType = {
  1: 'bgStairs',
  2: 'bgTalk',
  3: 'bgGroup',
};

const getBackgroundName = (currentStep: number): PeContentLayoutMarketingBannerProps['backgroundName'] =>
  backgroundStepMap[currentStep];

const getMarketingSloganMessage = (
  type: 'title' | 'desc',
  currentStep: number,
  formatMessage: ReturnType<typeof useIntlMessage>,
) => {
  const key = `slogan${type}step${currentStep}`;
  const message = messages[key as keyof typeof messages];

  return formatMessage(message);
};

const TokenError = () => <Error label={messages.tokenUsedLabel} text={messages.tokenUsed} />;

const PeOnboarding: FC = () => {
  const formatMessage = useIntlMessage();
  const [currentStep, setCurrentStep] = useState(1);
  const actions = {
    setCurrentStep,
  };
  const status = useSelector(getOnboardingStatus);

  const {
    query: { code, externalAuth: externalAuthQuery },
  } = useRouter();
  const confirmationCode = Array.isArray(code) ? code[0] : code;
  const isExternalAuth = Array.isArray(externalAuthQuery) ? externalAuthQuery[0] : externalAuthQuery;

  const dispatch = useDispatch<ThunkAppDispatch>();

  type ErrorList = {
    errors: Array<{ source: { pointer: string } }>;
  };

  useEffect(() => {
    if (isExternalAuth && confirmationCode) {
      dispatch(registrationDataRequested(confirmationCode))
        .unwrap()
        .catch(() => {
          dispatch(registrationDataRequestedFallback(confirmationCode));
        });
    }
  }, [isExternalAuth, confirmationCode, dispatch]);

  const initialValues = {
    page1: {
      salutation: '',
      title: '',
      firstName: '',
      lastName: '',
      password: '',
      externalAuth: isExternalAuth?.length > 0,
    },
    page2: {
      profileTitle: '',
    },
    page3: {
      profileIndustry: '',
      profileIndustryCustom: '',
    },
  };

  const isEmployerHost = useSelector(selectIsEmployerHost);

  const formik = useFormik({
    enableReinitialize: false,
    initialValues,
    validate: (values) => validatePassword(values),
    onSubmit: ({ page3, page1: { externalAuth, ...page1 }, page2 }: typeof initialValues) => {
      const page3Data = {
        ...page3,
        profileIndustry: page3.profileIndustry === 'profileIndustryCustom' ? undefined : page3.profileIndustry,
      };
      const cookies = new Cookies();

      cookies.remove('accessToken');
      cookies.remove('refreshToken');
      cookies.remove('profileId');
      cookies.remove('userId');
      cookies.remove('accountId');

      dispatch(
        onboardingFinished({
          ...page1,
          ...page2,
          ...page3Data,
          confirmationCode,
        }),
      )
        .unwrap()
        .then((response: yup.InferType<typeof trinityResponseSchema>) => {
          const appHost = getPublicEnvironmentValue('APP_HOST', isEmployerHost);

          if (appHost && window && response.data.attributes.status === 'success') {
            const domain = window.location.host
              .split('.')
              .slice(-2)
              .join('.');
            const options = { path: '/', domain: `${domain}`, secure: true };

            cookies.set('accessToken', response.data.attributes.accessToken, options);
            cookies.set('refreshToken', response.data.attributes.refreshToken, options);
            cookies.set('profileId', response.data.attributes.profileId, options);
            cookies.set('userId', response.data.attributes.userId, options);
            cookies.set('accountId', response.data.attributes.accountId, options);

            window.location.replace(`${appHost.replace(/\/$/, '')}/profile/create/1`);
          }
        })
        .catch((error: ErrorList) => {
          const isTokenUsed = error?.errors?.some(
            (item: { source: { pointer: string } }) => item?.source?.pointer === 'data/attributes/confirmationCode',
          );

          if (isTokenUsed) {
            return {
              error: { TokenError },
            };
          }

          return {
            error,
          };
        });
    },
  });

  return status === 'error' ? (
    <PeOnboardingError />
  ) : (
    <PeContentLayout
      marketingTitle={getMarketingSloganMessage('title', currentStep, formatMessage)}
      marketingDesc={getMarketingSloganMessage('desc', currentStep, formatMessage)}
      backgroundName={getBackgroundName(currentStep)}
    >
      <PeOnboardingWizard formik={formik} actions={actions} />
    </PeContentLayout>
  );
};

export default PeOnboarding;
